import {ValueObject} from '@s-ui/domain'

import {ConfigContext} from '../../config/context/index.js'
import {NumberSharedValueObject} from '../../shared/ValueObjects/NumberSharedValueObject.js'
import {StringSharedValueObject} from '../../shared/ValueObjects/StringSharedValueObject.js'

export class VehicleConfiguratorTrackingDataValueObject extends ValueObject {
  #event
  #hitOrigin
  #hitInformation
  #hitInformation2
  #brandId
  #modelId

  constructor({event, hitOrigin, hitInformation, hitInformation2, brandId, modelId}) {
    super()
    this.#event = event
    this.#hitOrigin = hitOrigin
    this.#hitInformation = hitInformation
    this.#hitInformation2 = hitInformation2
    this.#brandId = brandId
    this.#modelId = modelId
  }

  static validate({action, step, make, makeId, model, modelId, fuelType, trimLevel, engine, color, zipcode}) {
    if (!action) throw new Error('Action is required')
    if (!step) throw new Error('Step is required')

    const actions = Object.values(ConfigContext.get('configurator').VEHICLE_CONFIGURATOR_ACTIONS)
    if (!actions.includes(action)) throw new Error(`Action should be one of: ${actions.join(', ')}`)

    const steps = ConfigContext.get('configurator').VEHICLE_CONFIGURATOR_STEPS
    const stepsValues = Object.values(steps)
    if (step < 1 || step > stepsValues.length) throw new Error(`Steps should be between 1 and ${stepsValues.length}`)

    if (step === steps.MAKE_MODEL && action === 'next' && (!make || !model || !makeId || !modelId)) {
      throw Error(`For step ${step} and action ${action}, make and model are required`)
    }
    if (step === steps.FUEL_TYPE && action === 'next' && !fuelType) {
      throw Error(`For step ${step} and action ${action}, fuelType is required`)
    }
    if (step === steps.TRIM_LEVEL && action === 'next' && !trimLevel) {
      throw Error(`For step ${step} and action ${action}, trimLevel is required`)
    }
    if (step === steps.ENGINE && action === 'next' && !engine) {
      throw Error(`For step ${step} and action ${action}, engine is required`)
    }
    if (step === steps.COLOR && action === 'next' && !color) {
      throw Error(`For step ${step} and action ${action}, color is required`)
    }
    if (step === steps.ZIP_CODE && action === 'next' && !zipcode) {
      throw Error(`For step ${step} and action ${action}, zipcode is required`)
    }
  }

  static create({action, step, make, makeId, model, modelId, fuelType, trimLevel, engine, color, zipcode}) {
    VehicleConfiguratorTrackingDataValueObject.validate({
      action,
      step,
      make,
      makeId,
      model,
      modelId,
      fuelType,
      trimLevel,
      engine,
      color,
      zipcode
    })
    const actions = ConfigContext.get('configurator').VEHICLE_CONFIGURATOR_ACTIONS
    const steps = ConfigContext.get('configurator').VEHICLE_CONFIGURATOR_STEPS
    const {VEHICLE_CONFIGURATOR_NEXT_SUCCESS, VEHICLE_CONFIGURATOR_NEXT_ERROR, VEHICLE_CONFIGURATOR_BACK} =
      ConfigContext.get('tracking').TRACKING_EVENT_NAMES

    const EVENT_NAME_BY_ACTION = {
      [actions.NEXT]: VEHICLE_CONFIGURATOR_NEXT_SUCCESS,
      [actions.BACK]: VEHICLE_CONFIGURATOR_BACK,
      [actions.ERROR]: VEHICLE_CONFIGURATOR_NEXT_ERROR,
      [actions.RETRY]: VEHICLE_CONFIGURATOR_BACK
    }

    const HIT_ORIGIN_BY_STEP = {
      [actions.NEXT]: {
        [steps.MAKE_MODEL]: ({make, model, makeId, modelId}) => {
          return {
            hitOrigin: 'car configurator - oem and model',
            hitInformation: make,
            hitInformation2: model,
            brandId: makeId,
            modelId
          }
        },
        [steps.FUEL_TYPE]: ({fuelType}) => {
          return {hitOrigin: 'car configurator - fuelType', hitInformation: fuelType}
        },
        [steps.TRIM_LEVEL]: ({trimLevel}) => {
          return {hitOrigin: 'car configurator - trimLevel', hitInformation: trimLevel}
        },
        [steps.ENGINE]: ({engine}) => {
          return {hitOrigin: 'car configurator - engine', hitInformation: engine}
        },
        [steps.COLOR]: ({color}) => {
          return {hitOrigin: 'car configurator - color', hitInformation: color}
        },
        [steps.ZIP_CODE]: ({zipcode}) => {
          return {hitOrigin: 'car configurator - zipcode', hitInformation: zipcode}
        }
      },
      [actions.BACK]: {
        [steps.MAKE_MODEL]: () => {
          throw Error('Action back cant be performed on step 1 of configurator')
        },
        [steps.FUEL_TYPE]: () => {
          return {hitOrigin: 'car configurator - fuelType'}
        },
        [steps.TRIM_LEVEL]: () => {
          return {hitOrigin: 'car configurator - trimLevel'}
        },
        [steps.ENGINE]: () => {
          return {hitOrigin: 'car configurator - engine'}
        },
        [steps.COLOR]: () => {
          return {hitOrigin: 'car configurator - color'}
        },
        [steps.ZIP_CODE]: () => {
          return {hitOrigin: 'car configurator - zipcode'}
        }
      },
      [actions.ERROR]: {
        [steps.MAKE_MODEL]: ({make, model}) => {
          let hitInformation
          if (!model) hitInformation = 'model picker error'
          if (!make) hitInformation = 'make picker error'
          return {hitOrigin: 'car configurator - oem and model', hitInformation}
        },
        [steps.FUEL_TYPE]: ({fuelType}) => {
          let hitInformation
          if (!fuelType) hitInformation = 'fuelType picker error'
          return {hitOrigin: 'car configurator - fuelType', hitInformation}
        },
        [steps.TRIM_LEVEL]: ({trimLevel}) => {
          let hitInformation
          if (!trimLevel) hitInformation = 'trimLevel picker error'
          return {hitOrigin: 'car configurator - trimLevel', hitInformation}
        },
        [steps.ENGINE]: ({engine}) => {
          let hitInformation
          if (!engine) hitInformation = 'engine picker error'
          return {hitOrigin: 'car configurator - engine', hitInformation}
        },
        [steps.COLOR]: ({color}) => {
          let hitInformation
          if (!color) hitInformation = 'color picker error'
          return {hitOrigin: 'car configurator - color', hitInformation}
        },
        [steps.ZIP_CODE]: ({zipcode}) => {
          let hitInformation
          if (!zipcode) hitInformation = 'zipcode picker error'
          return {hitOrigin: 'car configurator - zipcode', hitInformation}
        }
      },
      [actions.RETRY]: {
        [steps.MAKE_MODEL]: ({make, model, makeId, modelId}) => {
          return {
            hitOrigin: 'car configurator - oem and model',
            hitInformation: 'error'
          }
        },
        [steps.FUEL_TYPE]: ({fuelType}) => {
          return {hitOrigin: 'car configurator - fuelType', hitInformation: 'error'}
        },
        [steps.TRIM_LEVEL]: ({trimLevel}) => {
          return {hitOrigin: 'car configurator - trimLevel', hitInformation: 'error'}
        },
        [steps.ENGINE]: ({engine}) => {
          return {hitOrigin: 'car configurator - engine', hitInformation: 'error'}
        },
        [steps.COLOR]: ({color}) => {
          return {hitOrigin: 'car configurator - color', hitInformation: 'error'}
        },
        [steps.ZIP_CODE]: ({zipcode}) => {
          return {hitOrigin: 'car configurator - zipcode', hitInformation: 'error'}
        }
      }
    }

    const event = EVENT_NAME_BY_ACTION[action]
    const data = HIT_ORIGIN_BY_STEP[action][step](...arguments)

    const hasBrandId = !NumberSharedValueObject.isEmpty({value: data.brandId})
    const hasModelId = !NumberSharedValueObject.isEmpty({value: data.modelId})

    return new VehicleConfiguratorTrackingDataValueObject({
      event: StringSharedValueObject.create({value: event}),
      hitOrigin: StringSharedValueObject.create({value: data.hitOrigin}),
      hitInformation: StringSharedValueObject.create({value: data.hitInformation}),
      hitInformation2: StringSharedValueObject.create({value: data.hitInformation2}),
      ...(hasBrandId && {
        brandId: NumberSharedValueObject.create({value: makeId})
      }),
      ...(hasModelId && {
        modelId: NumberSharedValueObject.create({value: modelId})
      })
    })
  }

  get event() {
    return this.#event.value
  }

  get hitOrigin() {
    return this.#hitOrigin.value
  }

  get hitInformation() {
    return this.#hitInformation.value
  }

  get hitInformation2() {
    return this.#hitInformation2.value
  }

  get brandId() {
    return this.#brandId?.value
  }

  get modelId() {
    return this.#modelId?.value
  }

  get value() {
    return {
      event: this.event,
      hitOrigin: this.hitOrigin,
      hitInformation: this.hitInformation,
      hitInformation2: this.hitInformation2,
      brandId: this.#brandId,
      modelId: this.#modelId
    }
  }
}
